import { CreateAnnouncementViewModel, EditAnnouncementViewModel, GetAnnouncementViewModel } from "@vintecc/capture-api-types/v1";
import { queryClient } from "@/queryClient";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@vintecc/components";
import axios from "axios";
import platformAdmin from "@/api/platformAdmin.api";
import { useCommonTranslation } from "@/services/culture/common.translations";

const getGatewayUpdatesQueryKey = (gatewayId: number) => ["gateways", "updates", gatewayId] as const;

export const requests = {
  key: "platformAdmin",
  logout: async () => {
    return await platformAdmin.logout();
  },
  getAnnouncements: async () => {
    const response = await platformAdmin.getAnnouncements();
    return response.data;
  },
  getEnabledAnnouncements: async () => {
    const response = await platformAdmin.getEnabledAnnouncements();
    return response.data;
  },
  toggleAnnouncement: async (id: number) => {
    const response = await platformAdmin.toggleAnnouncement(id);
    return response.data;
  },
  editAnnouncement: async (payload: EditAnnouncementViewModel) => {
    const response = await platformAdmin.editAnnouncement(payload);
    return response.data;
  },
  addAnnouncement: async (payload: CreateAnnouncementViewModel) => {
    const response = await platformAdmin.createAnnouncement(payload);
    return response.data;
  },
  deleteAnnouncement: async (id: number) => {
    const response = await platformAdmin.deleteAnnouncement(id);
    return response.data;
  },
  pushLoggerConfig: async (id: number) => {
    const response = await platformAdmin.pushLoggerConfig(id);
    if (response.status !== 200) {
      throw new Error("platformAdmin.pushLoggerConfigFailed");
    }

    return response.data;
  },
  pushUpdate: async ({
    deviceIds,
    updateId,
    updateBefore,
    updateAfter,
    triggerId,
  }: {
    deviceIds: number[];
    updateId: number;
    updateBefore?: string;
    updateAfter?: string;
    triggerId?: string;
  }) => {
    const response = await platformAdmin.pushUpdate({
      deviceIds,
      updateId,
      updateBefore,
      updateAfter,
      triggerId,
    });

    return response.data;
  },
  /**
   * This is used for tracking purposes when navigating to another app.
   */
  navigate: async ({ location, app, appId }: { location: string; app: boolean; appId?: number }) => {
    // Hidden in swagger config, but exists in the backend API
    const response = await axios.get<string>("/v1/PlatformAdmin/Navigate", {
      params: { location, app, ...(appId !== undefined && { appId }) },
    });
    return response.data;
  },
};

export const queries = {
  getAnnouncements: () => ({
    queryKey: [requests.key, "announcements"],
    queryFn: () => requests.getAnnouncements(),
  }),
  getEnabledAnnouncements: () => ({
    queryKey: [requests.key, "enabledAnnouncements"],
    queryFn: () => requests.getEnabledAnnouncements(),
  }),
  pushLoggerConfig: (id: number) => ({
    queryKey: [requests.key, "pushLoggerConfig", id],
    queryFn: () => requests.pushLoggerConfig(id),
    enabled: id !== null && id !== undefined && id > 0,
  }),
};

export const usePlatformAdminMutations = () => {
  const { openToast } = useToast();
  const { t } = useCommonTranslation();

  const logout = useMutation({
    mutationFn: requests.logout,
    onSuccess: () => {
      openToast({
        message: t("platformAdmin.logoutSuccess"),
        intent: "success",
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });

  const pushLoggerConfig = useMutation({
    mutationFn: requests.pushLoggerConfig,
    onSuccess: () => {
      openToast({
        message: t("platformAdmin.pushLoggerConfigSuccess"),
        intent: "success",
      });
    },
    onError: (e) => {
      const errorMessage = e instanceof Error ? e.message : String(e);
      openToast({
        message: errorMessage === "platformAdmin.pushLoggerConfigFailed" ? t("platformAdmin.pushLoggerConfigFailed") : errorMessage,
        intent: "error",
      });
    },
  });

  const pushUpdate = useMutation({
    mutationFn: requests.pushUpdate,
    onSuccess: async (_data, variables) => {
      const gatewayId = variables.deviceIds[0];
      await queryClient.invalidateQueries({
        queryKey: getGatewayUpdatesQueryKey(gatewayId),
      });
      openToast({
        message: t("platformAdmin.updatePushedSuccess"),
        intent: "success",
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });

  const navigate = useMutation({
    mutationFn: requests.navigate,
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });

  const toggleAnnouncement = useMutation({
    mutationFn: requests.toggleAnnouncement,
    onMutate: async (id: number) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      queryClient.cancelQueries({ queryKey: queries.getAnnouncements().queryKey });

      queryClient.setQueryData<GetAnnouncementViewModel[]>(queries.getAnnouncements().queryKey, (oldData) =>
        oldData?.map((announcement) => (announcement.id === id ? { ...announcement, enabled: !announcement.enabled } : announcement)),
      );
    },
    onSuccess: async (_data) => {
      queryClient.invalidateQueries({
        queryKey: queries.getAnnouncements().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries.getEnabledAnnouncements().queryKey,
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });
  const editAnnouncement = useMutation({
    mutationFn: requests.editAnnouncement,
    onSuccess: async (_data) => {
      await queryClient.invalidateQueries({
        queryKey: queries.getAnnouncements().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries.getEnabledAnnouncements().queryKey,
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });
  const addAnnouncement = useMutation({
    mutationFn: requests.addAnnouncement,
    onSuccess: async (_data) => {
      await queryClient.invalidateQueries({
        queryKey: queries.getAnnouncements().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries.getEnabledAnnouncements().queryKey,
      });
      openToast({
        message: t("platformAdmin.announcementCreatedSuccess"),
        intent: "success",
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });

  const deleteAnnouncement = useMutation({
    mutationFn: requests.deleteAnnouncement,
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: queries.getAnnouncements().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queries.getEnabledAnnouncements().queryKey,
      });
      openToast({
        message: t("platformAdmin.announcementDeletedSuccess"),
        intent: "success",
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });

  return {
    logout,
    pushLoggerConfig,
    pushUpdate,
    navigate,
    toggleAnnouncement,
    deleteAnnouncement,
    editAnnouncement,
    addAnnouncement,
  };
};
